import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { PRODUCTS } from "@/data/products";
import ProductCard from "@/components/ProductCard";
import { Reveal, StaggerItem } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { PAGE_SEO } from "@/lib/seo";

const Shop = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");

  const filters = [
    { key: "all", label: t.shop.all },
    { key: "supplement", label: t.shop.supplement },
    { key: "merch", label: t.shop.merch },
    { key: "accessory", label: t.shop.accessory },
  ];
  const list = filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);

  return (
    <div data-testid="page-shop">
      <Seo {...PAGE_SEO.shop} jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }])]} />
      <div className="bg-blush border-b border-ink py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <Reveal>
            <h1 className="display uppercase text-6xl md:text-8xl leading-[0.84] text-brand">{t.shop.title}</h1>
            <p className="mt-4 text-lg font-medium text-ink/70 max-w-md">{t.shop.sub}</p>
          </Reveal>
        </div>
      </div>

      <div className="bg-cream py-10 md:py-14">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-filters">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`btn h-10 px-5 text-xs ${filter === f.key ? "bg-ink text-cream" : "bg-cream text-ink hover:bg-ink hover:text-cream"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Link
            to="/bundle"
            data-testid="shop-bundle-cta"
            className="group border border-ink bg-limewash hover:bg-lime transition-colors mb-8 flex flex-col sm:flex-row items-center gap-4 p-5 md:p-6"
          >
            <span className="w-11 h-11 rounded-full bg-cream border border-ink flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-ink" strokeWidth={2} />
            </span>
            <div className="flex-1 text-center sm:text-left">
              <p className="display uppercase text-2xl md:text-3xl leading-none">{t.builder.title}</p>
              <p className="text-sm font-medium text-ink/60 mt-1">{t.builder.tierHint}</p>
            </div>
            <span className="btn bg-ink text-cream h-11 px-5 text-xs group-hover:bg-pink group-hover:text-cream">
              {t.builder.kicker} <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </span>
          </Link>

          <StaggerItem>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-ink border border-ink">
              {list.map((p) => (
                <ProductCard key={p.id} product={p} attached />
              ))}
            </div>
          </StaggerItem>
        </div>
      </div>

      <Marquee text={t.marquee} bg="bg-lime" color="text-ink" />
    </div>
  );
};

export default Shop;
